import React, { useState } from "react";
import { createPortal } from "react-dom";

const SortOrderHeader = ({ displayName, sortField, setSortOrderParent }) => {
  const [sortOrder, setSortOrder] = useState(null);

  const handleClickSort = () => {
    const newSort = sortOrder === "asc" ? "desc" : sortOrder === "desc" ? null : "asc";
    setSortOrder(newSort);
    setSortOrderParent?.(newSort ? { sort_by: sortField, sort_type: newSort } : null);
  };

  return (
    <div
      className="flex items-center gap-2 justify-content-between cursor-pointer select-none w-full"
      onClick={handleClickSort}
      title={`Klik untuk mengurutkan ${displayName}`}
    >
      <span className="font-semibold text-blue-600">{displayName}</span>
      {sortOrder && <i className={`text-gray-500 text-xs pi ${sortOrder === "asc" ? "pi-arrow-up" : "pi-arrow-down"}`}></i>}
    </div>
  );
};

const FilterOrderHeader = ({ displayName, id, openDropdown, setOpenDropdown, options = [], onSelectFilter }) => {
  const isOpen = openDropdown === id;
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectLabel, setSelectLabel] = useState(null);

  const toggleDropdown = (event) => {
    const rect = event.target.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenDropdown(isOpen ? null : id);
  };

  const handleSelect = (value) => {
    onSelectFilter?.(value);
    setSelectLabel(value);
    setOpenDropdown(null);
  };

  const handleReset = () => {
    onSelectFilter?.(null);
    setOpenDropdown(null);
    setSelectLabel(null);
  };

  const dropdown = (
    <div
      className="absolute z-[9999] bg-white shadow-lg p-3"
      style={{
        top: position.top,
        left: position.left,
        minWidth: "250px",
        width: "250px",
      }}
    >
      <h5 className="font-semibold mb-2">Filter {displayName}</h5>
      <div className="flex flex-wrap gap-2 mb-3">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`btn ${opt.value === selectLabel ? "btn-outline-primary" : "btn-primary"} btn-sm`}
            type="button"
            onClick={() => handleSelect(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex flex-row w-full justify-content-between align-items-center gap-2">
        <button className="btn btn-warning btn-sm w-100" type="button" onClick={() => setOpenDropdown(null)}>
          Tutup
        </button>
        <button className="btn btn-danger btn-sm w-100" type="button" onClick={() => handleReset()}>
          Reset
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full relative">
      <div className="flex flex-row align-items-center w-full justify-content-between cursor-pointer select-none">
        <span className="font-semibold text-blue-600">{displayName}</span>
        <button className="btn btn-primary btn-sm" type="button" onClick={toggleDropdown}>
          <i className="fa fa-filter"></i>
        </button>
      </div>
      {isOpen && createPortal(dropdown, document.body)}
    </div>
  );
};

function formatToIDR(value) {
  if (value == null || isNaN(value)) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value);
}

export const ColumnSalaryCalculation = (
  handleSortOrder,
) => {
  return [
    {
      headerName: "No",
      field: "no",
      width: 80,
      minWidth: 80,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Attendance Date",
      field: "attendanceDate",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Project",
      field: "sourceProject",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Project ID",
      field: "projectId",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      headerComponent: SortOrderHeader,
      headerComponentParams: {
        displayName: "Project ID",
        sortField: "project_id",
        setSortOrderParent: handleSortOrder,
      },
    },
    {
      headerName: "Client Name",
      field: "clientName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Worker Name",
      field: "workerName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Skill",
      field: "skillName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Order",
      field: "order",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Order Sukses",
      field: "orderSukses",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Total Tukang",
      field: "totalTukang",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueGetter: (params) => {
        const workerOnsite = params.data.workerOnsite ?? "-";
        const totalTukang = params.data.workerOrder ?? "-";
        return `${workerOnsite} / ${totalTukang}`;
      },
    },
    {
      headerName: "Sesi",
      field: "sesi",
      width: 125,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
    {
      headerName: "Upah Harian",
      field: "upahHarian",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Insentif",
      field: "upahInsentif",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Upah Transport",
      field: "upahTransport",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Upah Keramik",
      field: "upahKeramik",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Upah Las",
      field: "upahLas",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },

    {
      headerName: "Fee Building",
      field: "feeBuilding",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
    {
      headerName: "Total Upah",
      field: "totalUpah",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (p) => `Rp ${p.value.toLocaleString()}`,
      headerComponent: SortOrderHeader,
      headerComponentParams: {
        displayName: "Total Upah",
        sortField: "total_upah",
        setSortOrderParent: handleSortOrder,
      },
    },
    {
      headerName: "Tabungan / Asuransi",
      field: "insurance",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
    },
  ];
};
